import React, { useState, useEffect, useCallback } from 'react';
import { Image, X, ChevronLeft, ChevronRight, Calendar, User, Scissors, Filter, Heart, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import apiService from './services/api';
import { LoadingSpinner } from './components';

function PhotoGalleryPage() {
  const navigate = useNavigate();
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextPage, setNextPage] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [technicians, setTechnicians] = useState([]);
  const [filters, setFilters] = useState({ technician: '', date_from: '', date_to: '', client: '' });
  const [showFilters, setShowFilters] = useState(false);
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    loadPhotos();
    loadTechnicians();
  }, []);

  const buildParams = (extra = {}) => {
    const params = { page_size: 24, ...extra };
    if (filters.technician) params.technician = filters.technician;
    if (filters.date_from) params.date_from = filters.date_from;
    if (filters.date_to) params.date_to = filters.date_to;
    if (filters.client) params.client = filters.client;
    if (activeTab === 'favorites' || extra._favorites) params.favorites = 'true';
    return params;
  };

  const loadPhotos = async (extra = {}) => {
    setLoading(true);
    try {
      const data = await apiService.get('/amn/appointment-pictures/gallery/', buildParams(extra));
      setPhotos(data.results || []);
      setNextPage(data.next);
      setTotalCount(data.count || 0);
    } catch (err) {
      console.error('Failed to load photos:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadMore = async () => {
    if (!nextPage || loadingMore) return;
    setLoadingMore(true);
    try {
      const url = new URL(nextPage);
      const params = Object.fromEntries(url.searchParams.entries());
      const data = await apiService.get('/amn/appointment-pictures/gallery/', params);
      setPhotos(prev => [...prev, ...(data.results || [])]);
      setNextPage(data.next);
    } catch (err) {
      console.error('Failed to load more:', err);
    } finally {
      setLoadingMore(false);
    }
  };

  const loadTechnicians = async () => {
    try {
      const data = await apiService.getTechnicians();
      const techs = (data.results || data || []).filter(t => t.square_status === 'active' && t.internal_status !== 'inactive');
      setTechnicians(techs);
    } catch (err) {
      console.error('Failed to load technicians:', err);
    }
  };

  const toggleFavorite = async (e, photoId) => {
    e.stopPropagation();
    try {
      const result = await apiService.post(`/amn/appointment-pictures/${photoId}/toggle-favorite/`);
      setPhotos(prev => prev.map(p => p.id === photoId ? { ...p, is_favorite: result.is_favorite } : p).filter(p => activeTab !== 'favorites' || p.is_favorite));
    } catch (err) {
      console.error('Failed to toggle favorite:', err);
    }
  };

  const applyFilters = () => loadPhotos();

  const clearFilters = () => {
    setFilters({ technician: '', date_from: '', date_to: '', client: '' });
    loadPhotos({ _clear: true });
  };

  const switchTab = (tab) => {
    setActiveTab(tab);
    const extra = tab === 'favorites' ? { _favorites: true } : {};
    loadPhotos(extra);
  };

  const hasActiveFilters = filters.technician || filters.date_from || filters.date_to || filters.client;

  const openLightbox = (index) => setSelectedPhoto(index);
  const closeLightbox = () => setSelectedPhoto(null);

  const navigate_photo = useCallback((dir) => {
    setSelectedPhoto(prev => {
      if (prev === null) return null;
      const next = prev + dir;
      if (next < 0 || next >= photos.length) return prev;
      return next;
    });
  }, [photos.length]);

  useEffect(() => {
    if (selectedPhoto === null) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') navigate_photo(-1);
      if (e.key === 'ArrowRight') navigate_photo(1);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [selectedPhoto, navigate_photo]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const photosByDate = photos.reduce((groups, photo) => {
    const date = photo.appointment_date ? new Date(photo.appointment_date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }) : 'Unknown Date';
    if (!groups[date]) groups[date] = [];
    groups[date].push(photo);
    return groups;
  }, {});

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 bg-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Image className="w-5 h-5 text-gray-600" />
            <h1 className="text-lg font-semibold text-gray-900">Photo Gallery</h1>
            {totalCount > 0 && (
              <span className="text-sm text-gray-500">({totalCount.toLocaleString()})</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {/* Tabs */}
            <div className="flex gap-1 bg-gray-100 rounded-lg p-0.5">
              <button
                onClick={() => switchTab('all')}
                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                  activeTab === 'all' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                All
              </button>
              <button
                onClick={() => switchTab('favorites')}
                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors flex items-center gap-1 ${
                  activeTab === 'favorites' ? 'bg-white text-red-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Heart className="w-3 h-3" />
                Favorites
              </button>
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                hasActiveFilters
                  ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Filter className="w-4 h-4" />
              <span className="hidden sm:inline">Filters</span>
              {hasActiveFilters && <span className="w-2 h-2 bg-blue-600 rounded-full" />}
            </button>
          </div>
        </div>

        {/* Filters */}
        {showFilters && (
          <div className="mt-3 flex flex-wrap gap-3 items-end">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Client</label>
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <input
                  type="text"
                  value={filters.client}
                  onChange={(e) => setFilters(prev => ({ ...prev, client: e.target.value }))}
                  placeholder="Client name..."
                  className="text-sm border border-gray-300 rounded-lg pl-7 pr-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500 w-40"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Technician</label>
              <select
                value={filters.technician}
                onChange={(e) => setFilters(prev => ({ ...prev, technician: e.target.value }))}
                className="text-sm border border-gray-300 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All</option>
                {technicians.map(tech => {
                  const name = tech.person
                    ? `${tech.person.first_name || ''} ${tech.person.last_name || ''}`.trim()
                    : `Staff #${tech.id}`;
                  return <option key={tech.id} value={tech.id}>{name}</option>;
                })}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">From</label>
              <input
                type="date"
                value={filters.date_from}
                onChange={(e) => setFilters(prev => ({ ...prev, date_from: e.target.value }))}
                className="text-sm border border-gray-300 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">To</label>
              <input
                type="date"
                value={filters.date_to}
                onChange={(e) => setFilters(prev => ({ ...prev, date_to: e.target.value }))}
                className="text-sm border border-gray-300 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <button
              onClick={applyFilters}
              className="px-3 py-1.5 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Apply
            </button>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-800 transition-colors"
              >
                Clear
              </button>
            )}
          </div>
        )}
      </div>

      {/* Gallery */}
      <div className="flex-1 overflow-y-auto px-4 py-4">
        {loading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : photos.length === 0 ? (
          <div className="text-center py-12">
            {activeTab === 'favorites' ? (
              <>
                <Heart className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-sm text-gray-500">No favorite photos yet</p>
                <p className="text-xs text-gray-400 mt-1">Click the heart icon on any photo to add it to favorites</p>
              </>
            ) : (
              <>
                <Image className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-sm text-gray-500">No photos found</p>
              </>
            )}
          </div>
        ) : (
          <>
            {Object.entries(photosByDate).map(([date, datePhotos]) => (
              <div key={date} className="mb-6">
                <h2 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-1.5">
                  <Calendar className="w-3.5 h-3.5 text-gray-400" />
                  {date}
                  <span className="text-xs text-gray-400 font-normal">({datePhotos.length})</span>
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                  {datePhotos.map((photo) => {
                    const globalIndex = photos.indexOf(photo);
                    return (
                      <div
                        key={photo.id}
                        className="group relative aspect-square rounded-lg overflow-hidden bg-gray-100 cursor-pointer hover:ring-2 hover:ring-blue-500 transition-all"
                        onClick={() => openLightbox(globalIndex)}
                      >
                        <img
                          src={photo.thumbnail_url || photo.image_url}
                          alt={`${photo.client_name || 'Appointment'} - ${photo.services || ''}`}
                          className="w-full h-full object-cover"
                          loading="lazy"
                          decoding="async"
                        />
                        {/* Favorite button */}
                        <button
                          onClick={(e) => toggleFavorite(e, photo.id)}
                          className={`absolute top-2 right-2 p-1.5 rounded-full transition-all ${
                            photo.is_favorite
                              ? 'bg-red-500 text-white opacity-100'
                              : 'bg-black/40 text-white opacity-0 group-hover:opacity-100'
                          }`}
                        >
                          <Heart className={`w-3.5 h-3.5 ${photo.is_favorite ? 'fill-current' : ''}`} />
                        </button>
                        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          {photo.client_name && (
                            <p className="text-xs text-white font-medium truncate">{photo.client_name}</p>
                          )}
                          {photo.technician_name && (
                            <p className="text-[10px] text-white/80 truncate">{photo.technician_name}</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            {nextPage && (
              <div className="text-center py-6">
                <button
                  onClick={loadMore}
                  disabled={loadingMore}
                  className="px-4 py-2 text-sm font-medium bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  {loadingMore ? 'Loading...' : `Load More (${photos.length} of ${totalCount.toLocaleString()})`}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Lightbox */}
      {selectedPhoto !== null && photos[selectedPhoto] && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center" onClick={closeLightbox}>
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 p-2 text-white/70 hover:text-white transition-colors z-10"
          >
            <X className="w-6 h-6" />
          </button>

          {/* Favorite button in lightbox */}
          <button
            onClick={(e) => { e.stopPropagation(); toggleFavorite(e, photos[selectedPhoto].id); }}
            className={`absolute top-4 left-4 p-2.5 rounded-full transition-all z-10 ${
              photos[selectedPhoto].is_favorite
                ? 'bg-red-500 text-white'
                : 'bg-white/20 text-white hover:bg-white/30'
            }`}
          >
            <Heart className={`w-5 h-5 ${photos[selectedPhoto].is_favorite ? 'fill-current' : ''}`} />
          </button>

          {selectedPhoto > 0 && (
            <button
              onClick={(e) => { e.stopPropagation(); navigate_photo(-1); }}
              className="absolute left-4 top-1/2 -translate-y-1/2 p-2 text-white/70 hover:text-white transition-colors z-10"
            >
              <ChevronLeft className="w-8 h-8" />
            </button>
          )}

          {selectedPhoto < photos.length - 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); navigate_photo(1); }}
              className="absolute right-4 top-1/2 -translate-y-1/2 p-2 text-white/70 hover:text-white transition-colors z-10"
            >
              <ChevronRight className="w-8 h-8" />
            </button>
          )}

          <div className="max-w-5xl max-h-[85vh] flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
            <img
              src={photos[selectedPhoto].image_url}
              alt=""
              className="max-w-full max-h-[75vh] object-contain rounded-lg"
            />
            <div className="mt-3 text-center">
              <div className="flex items-center justify-center gap-4 text-sm text-white/90">
                {photos[selectedPhoto].client_name && (
                  <span className="flex items-center gap-1">
                    <User className="w-3.5 h-3.5" />
                    {photos[selectedPhoto].client_name}
                  </span>
                )}
                {photos[selectedPhoto].technician_name && (
                  <span className="flex items-center gap-1">
                    <Scissors className="w-3.5 h-3.5" />
                    {photos[selectedPhoto].technician_name}
                  </span>
                )}
                <span className="flex items-center gap-1">
                  <Calendar className="w-3.5 h-3.5" />
                  {formatDate(photos[selectedPhoto].appointment_date)}
                </span>
              </div>
              {photos[selectedPhoto].services && (
                <p className="text-xs text-white/60 mt-1">{photos[selectedPhoto].services}</p>
              )}
              <button
                onClick={() => navigate(`/appointments/${photos[selectedPhoto].appointment_id}`)}
                className="mt-2 text-xs text-blue-400 hover:text-blue-300 transition-colors"
              >
                View Appointment
              </button>
            </div>
          </div>

          <div className="absolute bottom-4 text-sm text-white/50">
            {selectedPhoto + 1} / {photos.length}
          </div>
        </div>
      )}
    </div>
  );
}

export default PhotoGalleryPage;
